import { motion } from "framer-motion";
import { Reveal, EASE } from "@/lib/motion";
import { PageHero, PrimaryCTAs, SectionHeader, FaqBlock } from "@/components/site/PageParts";
import { CTABand } from "@/components/site/Bands";
import { SEO, breadcrumbSchema, faqSchema, serviceSchema } from "@/components/site/SEO";
import { RelatedWork } from "@/components/site/RelatedWork";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Services", path: "/services" },
  { name: "Content Operations", path: "/services/content-operations" },
];

const RELATED = [
  { tag: "Case Study", title: "JustCall — 4 → 20 pieces / month", desc: "The exact operational scale-up powered by these systems.", to: "/case-studies" },
  { tag: "Process", title: "The Dandelion Method", desc: "The framework the operational engine runs on.", to: "/how-we-work" },
  { tag: "Service", title: "Strategic Content Planning", desc: "Where every operational engagement starts.", to: "/services/content-strategy" },
];

const WALL = [
  "Hiring more writers alone leads to inconsistent quality.",
  "Approval bottlenecks slow every draft.",
  "No formal QA — bugs ship, then get caught in public.",
  "Knowledge lives in one person's head and vanishes when they leave.",
  "ROI declines as you add volume, instead of improving.",
];

const COMPONENTS = [
  { n: "01", title: "Brand Playbook", desc: "20–30 pages codifying tone, style, SEO requirements, quality checklist, and good/bad examples." },
  { n: "02", title: "Content Templates & Frameworks", desc: "Article structure templates, outline templates, research frameworks, brief templates." },
  { n: "03", title: "Workflow & Approval Processes", desc: "Production workflow, approval process, escalation paths, communication protocols." },
  { n: "04", title: "Quality Control Framework", desc: "Outline review, draft review, SEO review, final QA checklist — every stage owned." },
  { n: "05", title: "Production Tracking & Metrics", desc: "Production dashboard, performance tracking, velocity and quality metrics you can defend." },
  { n: "06", title: "Team Training & Onboarding", desc: "Writer onboarding guide, stakeholder training, process documentation, recorded walkthroughs." },
];

const PROCESS = [
  { w: "Week 1–2", title: "Current-State Assessment", body: "Audit existing content and processes, stakeholder interviews, identify bottlenecks." },
  { w: "Week 3–4", title: "System Design", body: "Brand playbook draft, workflow design, templates." },
  { w: "Week 5–6", title: "Implementation & Training", body: "Finalize documentation, train team, set up tracking, pilot with 2–3 pieces." },
];

const FAQS = [
  { q: "Can we do this ourselves?", a: "You can, but it typically takes 6–12 months of trial and error. We've done this repeatedly and bring proven frameworks." },
  { q: "What if we're already publishing 20+ pieces / month?", a: "We audit what you have, identify gaps, and upgrade what's broken — usually quality control and metrics at that scale." },
];

export default function ContentOperations() {
  return (
    <div data-testid="service-operations-page">
      <SEO
        title="Content Operations & Scaling for B2B SaaS"
        description="Scale from 4 to 40 pieces of content per month without dropping quality. Brand playbook, templates, QA framework, tracking, and team training in 4–6 weeks."
        path="/services/content-operations"
        jsonLd={[breadcrumbSchema(CRUMBS), faqSchema(FAQS), serviceSchema({ name: "Content Operations & Scaling", description: "Operational infrastructure to scale B2B SaaS content production without quality drops.", path: "/services/content-operations" })]}
      />
      <PageHero
        eyebrow="Services · Content Operations"
        lines={["Scale From 4", "Articles to 40"]}
        italicLast="Without Breaking."
        sub="Most companies hit a wall around 10–15 pieces per month. Quality drops. Timelines slip. The problem is the systems. We build the operational infrastructure that makes sustainable scaling possible."
        crumbs={CRUMBS}
      >
        <PrimaryCTAs />
      </PageHero>

      {/* The wall */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="The scaling wall" title="Why volume kills quality." />
            <p className="mt-6 max-w-md text-[#666666] leading-relaxed">
              The fix isn&apos;t hiring more people. It&apos;s systems that let your people ship faster without losing the plot.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="border-t border-[#E0E0E0]">
              {WALL.map((g, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: EASE }}
                  className="py-5 border-b border-[#E0E0E0] flex items-start gap-4 text-[#111111]"
                >
                  <span className="font-mono text-xs text-[#999999] mt-2">0{i + 1}</span>
                  <span className="text-lg leading-relaxed">{g}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Components */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="What we build" title="Six systems, one operational engine." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#E0E0E0]">
            {COMPONENTS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.05}>
                <div className="p-8 md:p-10 border-r border-b border-[#E0E0E0] h-full">
                  <span className="font-mono text-xs text-[#999999]">{c.n}</span>
                  <h3 className="mt-5 font-serif text-2xl text-[#111111]">{c.title}</h3>
                  <p className="mt-4 text-[#666666] leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-pad bg-[#111111] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-[#666666] mb-6 font-mono">Process · 4–6 weeks</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Assessment to trained team in six weeks.</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 border-t border-[#333333]">
            {PROCESS.map((p, i) => (
              <Reveal key={p.w} delay={i * 0.08}>
                <div className={`py-10 ${i !== 0 ? "md:pl-10" : ""} md:pr-10 ${i !== PROCESS.length - 1 ? "md:border-r border-[#333333] border-b md:border-b-0" : ""}`}>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#666666]">{p.w}</p>
                  <h3 className="mt-4 font-serif text-3xl text-white">{p.title}</h3>
                  <p className="mt-5 text-[#E0E0E0] leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Proof callout */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="bg-[#111111] text-white p-10 md:p-16 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.25em] text-[#666666] font-mono">Proof point</p>
              <p className="mt-6 font-serif text-8xl md:text-9xl leading-none">5×</p>
              <p className="mt-6 font-serif text-2xl md:text-3xl leading-[1.3] text-white max-w-4xl">
                We scaled JustCall from 4 to 20 pieces of content per month, maintaining client satisfaction throughout.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHeader eyebrow="FAQ" title="Common questions." />
          <div className="mt-12">
            <FaqBlock items={FAQS} />
          </div>
        </div>
      </section>

      <CTABand heading="Scale content and organic reach without dropping quality." />
      <RelatedWork items={RELATED} />
    </div>
  );
}
